// src/components/Shop/LastVisitedItems.tsx - Updated for small horizontal layout
import React, { useState, useEffect } from 'react';
import { Product } from './index';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import { useLanguage } from '../../context/LanguageContext';
import './LastVisitedItems.css';

interface LastVisitedItemsProps {
  onProductSelect?: (product: Product) => void;
}

const LastVisitedItems: React.FC<LastVisitedItemsProps> = ({
  onProductSelect
}) => {
  const { language } = useLanguage();
  const [visitedProducts, setVisitedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Load visited products from localStorage
  const loadVisitedProducts = () => {
    try {
      setLoading(true);
      const products = productService.visitedProducts.getVisitedProducts();
      console.log('📦 Loaded visited products:', products.length);
      setVisitedProducts(products);
    } catch (error) {
      console.error('Error loading visited products:', error);
      setVisitedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVisitedProducts();

    // Listen for storage changes to update when products are added from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'lastVisitedProducts') {
        loadVisitedProducts();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Don't render if loading or no visited products
  if (loading) {
    return (
      <div className="last-visited-items-section">
        <div className="last-visited-items-container">
          <div className="visited-loading">
            <div className="visited-loading-spinner"></div>
            <p>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (visitedProducts.length === 0) {
    return null;
  }

  const handleProductClick = (product: Product) => {
    if (onProductSelect) {
      onProductSelect(product);
    }
    // Navigate to product page
    window.location.href = `/shop/product/${product.id}`;
  };

  return (
    <div className="last-visited-items-section">
      <div className="last-visited-items-container">

        {/* Horizontal scrolling grid */}
        <div className="last-visited-grid">
          {visitedProducts.map((product) => (
            <div
              key={product.id}
              onClick={() => handleProductClick(product)}
              className="visited-product-card"
            >

              {/* Product Image */}
              <div className="visited-product-image">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images.find(img => img.isMain)?.url || product.images[0].url}
                    alt={product.images[0].alt || product.titleEn}
                    className="visited-product-img"
                    loading="lazy"
                  />
                ) : (
                  <div className="visited-product-placeholder">
                    <span className="placeholder-icon">
                      {categoryService.getCategoryIcon(product.category) || '📦'}
                    </span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="visited-product-info">
                <h4 className="visited-product-title">
                  {language === 'ar' && product.titleAr ? product.titleAr : product.titleEn}
                </h4>

                {product.price && (
                  <span className="visited-product-price">
                    {product.price} ₪
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LastVisitedItems;